const integer = (value) => /^\d+$/.test(String(value)) && Number.isSafeInteger(Number(value));
const amount = (value) =>
  value != null &&
  String(value).trim() !== "" &&
  Number.isFinite(Number(value)) &&
  Number(value) >= 0;

// Each response keeps its own file and toll breakdown. Never use value_concil
// to recover a toll's amount: partial responses deliberately have a zero total.
export function reportedPeajeDetails(response) {
  const reports = response?.reports || (response ? [response] : []);
  const result = {};
  [...reports]
    .sort(
      (a, b) =>
        (Date.parse(a.updatedAt || a.createdAt) || Number(a.id) || 0) -
        (Date.parse(b.updatedAt || b.createdAt) || Number(b.id) || 0)
    )
    .forEach((report) => {
      if (!report.fileName && !report.files?.length) return;
      Object.entries(report.more || {}).forEach(([peaje, detail]) => {
        const explicitFiles = report.files?.some((file) => file.peaje);
        const file = report.files?.find((file) => file.peaje === peaje);
        const fileName =
          explicitFiles || report.files?.length > 1 ? file?.fileName || "" : report.fileName;
        const filePeaje = file?.peaje;

        const entries = detail?.steps != null ? [["", detail]] : Object.entries(detail || {});
        const previouslyReported = Boolean(result[peaje]);
        entries.forEach(([llave, value]) => {
          if (!integer(value?.steps) || !amount(value?.value)) return;
          if (!result[peaje]) result[peaje] = {};
          const previous = result[peaje][llave];
          if (previouslyReported) {
            if (
              previous &&
              explicitFiles &&
              report.id != null &&
              (previous.responseId === report.id ||
                (previous.responseId == null &&
                  previous.fileName === fileName &&
                  previous.steps === Number(value.steps) &&
                  previous.value === Number(value.value)))
            ) {
              previous.fileName = fileName;
              previous.filePeaje = filePeaje;
              previous.responseId = filePeaje ? report.id : undefined;
            }
            if (
              previous &&
              previous.responseId == null &&
              report.id != null &&
              (!(explicitFiles || report.files?.length > 1) || filePeaje) &&
              previous.fileName === fileName &&
              previous.steps === Number(value.steps) &&
              previous.value === Number(value.value)
            )
              previous.responseId = report.id;
            return;
          }
          result[peaje][llave] = {
            steps: Number(value.steps),
            value: Number(value.value),
            fileName,
            ...(filePeaje ? { filePeaje } : {}),
            observation: value.observation ?? report.observation ?? "",
            payload: { ...value },
            sourceKey: report.key_concil,
            responseId:
              ((explicitFiles || report.files?.length > 1) && !filePeaje ? undefined : report.id) ??
              (previous?.fileName === fileName &&
              previous.steps === Number(value.steps) &&
              previous.value === Number(value.value)
                ? previous.responseId
                : undefined),
          };
        });
      });
    });
  return result;
}

export function reportedDetailForRow(rows, row, saved) {
  const details = saved[row.peaje];
  if (!details) return undefined;
  if (details[row.llave]) return details[row.llave];
  // A response may store its aggregate key instead of the descriptive shift.
  // Only associate that total when the toll has one unambiguous shift.
  if (rows.filter((item) => item.peaje === row.peaje).length !== 1) return undefined;
  if (details[""]) return details[""];
  const entries = Object.entries(details);
  if (entries.length !== 1) return undefined;
  const [storedKey, detail] = entries[0];
  const canonical = (key) => String(key || "").replace(/^PRE_/, "");
  return (/^(?:PRE_)?\d{10,}$/.test(storedKey) &&
    canonical(storedKey) === canonical(detail.sourceKey)) ||
    (/^(?:PRE_)?\d{10,}$/.test(row.llave) && canonical(row.llave) === canonical(detail.sourceKey))
    ? detail
    : undefined;
}

export function buildPeajePayment(rows, peaje, response) {
  if (!rows.length) throw new Error("Espera a que se carguen los peajes.");
  const saved = reportedPeajeDetails(response);
  const detailFor = (row) => reportedDetailForRow(rows, row, saved);
  const selected = rows.filter((row) => row.peaje === peaje);
  if (!selected.length) throw new Error("Selecciona el peaje que vas a reportar.");
  if (selected.some(detailFor))
    throw new Error("Este peaje ya tiene información enviada y no se puede editar.");
  const more = Object.fromEntries(
    [...new Set([...rows.map((row) => row.peaje), ...Object.keys(saved)])].map((name) => [
      name,
      saved[name]
        ? Object.fromEntries(
            Object.entries(saved[name]).map(([llave, detail]) => [
              llave,
              {
                ...detail.payload,
                steps: detail.steps,
                value: detail.value,
                ...(detail.observation ? { observation: detail.observation } : {}),
              },
            ])
          )
        : null,
    ])
  );
  more[peaje] = {};
  let steps = 0;
  selected.forEach((row) => {
    if (!integer(row.steps)) throw new Error(`Completa los pasos de ${peaje}.`);
    if (!/^\d+(\.\d{1,2})?$/.test(String(row.value)))
      throw new Error(`Completa el valor total de ${peaje}.`);
    steps += Number(row.steps);
    more[peaje][row.llave] = { steps: Number(row.steps), value: Number(row.value) };
  });
  const complete = rows.every((row) => row.peaje === peaje || detailFor(row));
  const totalSteps = complete
    ? rows.reduce(
        (total, row) =>
          total + (row.peaje === peaje ? more[peaje][row.llave] : detailFor(row)).steps,
        0
      )
    : 0;
  const totalCents = complete
    ? rows.reduce(
        (total, row) =>
          total +
          Math.round((row.peaje === peaje ? more[peaje][row.llave] : detailFor(row)).value * 100),
        0
      )
    : 0;
  if (
    !Number.isSafeInteger(totalCents) ||
    !Number.isSafeInteger(steps) ||
    !Number.isSafeInteger(totalSteps)
  )
    throw new Error("Los totales exceden el valor permitido.");
  return { more, steps: totalSteps, value_concil: (totalCents / 100).toFixed(2), complete };
}
